import { SafetyPlanResponseOptionType } from "../../../../utils/constants"
import {
  ResponseOption,
  SafetyPlanProps,
} from "../../../../utils/safetyPlanUtils"

import ContactOption from "./ContactOption"
import PlaceOption from "./PlaceOption"
import RadioOption from "./RadioOption"
import SelectAllOption from "./SelectAllOption"
import TextBoxOption from "./TextBoxOption"
import UserAddedOption from "./UserAddedOption"

/**
 * Renders the correct input control for a single response option based on
 * its category type. Handles all known SafetyPlanResponseOptionType values:
 *
 * - TEXT_BOX    → free-text input
 * - RADIO       → segmented radio selector
 * - CONTACT     → contact name + phone input
 * - USER_ADDED  → read-only display of previously added values
 * - PLACE       → place name input with AddPlaceDialog
 * - SELECT_ALL  → toggleable chip/button
 *
 * @param option     - The response option(s) to render. Pass a single ResponseOption
 *                     for most types; pass ResponseOption[] for USER_ADDED.
 * @param onClick    - Callback invoked when the user selects or toggles an option.
 * @param type       - The category type string that determines which input control is rendered.
 * @param selection  - The current list of selected items used to reflect saved state.
 * @param onChange   - Callback invoked when free-text content changes (TEXT_BOX only).
 * @param newSafetyPlanView - Right now some components render differently based on these two views.
 * This is used to set some flags that cause some options to render differently if this is the case.
 */
export default function Options({
  option,
  onClick,
  type,
  selection,
  onChange,
  newSafetyPlanView,
}: SafetyPlanProps) {
  switch (type) {
    case SafetyPlanResponseOptionType.TEXT_BOX:
      return (
        <TextBoxOption
          option={option as ResponseOption}
          onChange={onChange}
          type={type}
          allowMultiple={newSafetyPlanView}
        />
      )
    case SafetyPlanResponseOptionType.RADIO:
      return (
        <RadioOption
          option={option as ResponseOption}
          type={type}
          selection={selection}
          onClick={onClick}
        />
      )
    case SafetyPlanResponseOptionType.CONTACT:
      return (
        <ContactOption
          option={option as ResponseOption}
          type={type}
          selection={selection}
          onClick={onClick}
        />
      )
    case SafetyPlanResponseOptionType.USER_ADDED:
      return (
        <UserAddedOption
          options={option as ResponseOption[]}
          selection={selection}
        />
      )
    case SafetyPlanResponseOptionType.PLACE:
      return (
        <PlaceOption
          option={option as ResponseOption}
          type={type}
          selection={selection}
          onClick={onClick}
        />
      )
    case SafetyPlanResponseOptionType.SELECT_ALL:
      return (
        <SelectAllOption
          option={option as ResponseOption}
          type={type}
          selection={selection}
          onClick={onClick}
        />
      )
    default:
      return <></>
  }
}
